import type { Step as RcStep } from '@sb/remote-control/types';
import type { Space } from '@sb/routine-runner';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class UpdateVisualizationRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.UpdateVisualization', 'UpdateVisualization');
  }

  protected validate({ args, routine, ctx }: CanonicalValidateArgs): void {
    type UpdateVisualizationArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'UpdateVisualization' }
    >;
    const a = args as UpdateVisualizationArgs;
    this.checkTargetItemPresent(a);
    const allSpaceItems = this.getSpaceItems(routine, ctx);
    const targetItem = this.findTargetItem(allSpaceItems, a);
    this.checkTargetExists(targetItem);
    this.checkPalletizing(a);
    this.checkPositionList(a, targetItem);
  }

  private checkTargetItemPresent(
    args: Extract<RcStep.Arguments, { argumentKind: 'UpdateVisualization' }>,
  ): void {
    /**
     * Ensure targetSpaceItemId is set.
     * Throws ActionRequiredError if missing.
     *
     * @throws {ActionRequiredError}
     */
    if (!args.targetSpaceItemId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Target space item required',
        fieldId: 'targetSpaceItemId',
      });
    }
  }

  private findTargetItem(
    allSpaceItems: Space.Item[],
    args: Extract<RcStep.Arguments, { argumentKind: 'UpdateVisualization' }>,
  ): Space.Item | undefined {
    /**
     * Find target space item by id.
     *
     * @returns {any|undefined} The target item if found
     */
    return allSpaceItems.find((item) => item.id === args.targetSpaceItemId);
  }

  private checkTargetExists(targetItem: Space.Item | undefined): void {
    /**
     * Ensure the referenced target item exists.
     * Throws ActionRequiredError if not found.
     *
     * @throws {ActionRequiredError}
     */
    if (!targetItem) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Target space item not found',
        fieldId: 'targetSpaceItemId',
      });
    }
  }

  private checkPalletizing(
    args: Extract<RcStep.Arguments, { argumentKind: 'UpdateVisualization' }>,
  ): void {
    /**
     * Validate palletizing parameters when provided.
     * Throws ActionRequiredError if key indices are missing.
     *
     * @throws {ActionRequiredError}
     */
    if (!args.palletizing || args.targetKind !== 'space') return;

    if (
      (args.palletizing.scope === 'allBoxesInLayer' ||
        args.palletizing.scope === 'specificBox') &&
      !args.palletizing.layerIndex
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Layer index required',
        fieldId: 'palletizing.layerIndex',
      });
    }

    if (
      args.palletizing.scope === 'specificBox' &&
      !args.palletizing.boxIndex
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Box index required',
        fieldId: 'palletizing.boxIndex',
      });
    }
  }

  private checkPositionList(
    args: Extract<RcStep.Arguments, { argumentKind: 'UpdateVisualization' }>,
    targetItem: Space.Item | undefined,
  ): void {
    /**
     * Validate position list indices for supported space item kinds.
     * Throws ActionRequiredError if the required indices are missing.
     *
     * @throws {ActionRequiredError}
     */
    if (!args.positionList) return;

    if (
      targetItem &&
      (targetItem.kind === 'freeformPositionList' ||
        targetItem.kind === 'gridPositionList') &&
      args.positionList.positionScope === 'specificPosition' &&
      !args.positionList.positionIndex
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position index required',
        fieldId: 'positionList.positionIndex',
      });
    }
  }
}

export const UpdateVisualizationValidation: ValidationRule =
  new UpdateVisualizationRule().toValidationRule();
